import { useCallback, useMemo } from "react";

import { AutoSizer, VirtualTable } from "@snapchat/snap-design-system";

import { clickableRowCss } from "../../../../common/components/table/styles";
import { type RangeModifier } from "../../../../connections/models";
import { useSelectedOrganization } from "../../../../organizations/hooks";
import { type LensInGroupModel } from "../../../model";
import { type LensUpdateData } from "../../models";

import { getColumns } from "./GroupLensesTableColumns";

const noop = () => Promise.resolve([]);

interface GroupLensesTableProps {
    lenses: Readonly<LensInGroupModel[]>;
    readonly: boolean;
    onLensRemove: (lensId: string) => void;
    onLensChange: (data: LensUpdateData) => void;
    onLensClick: (lens: LensInGroupModel) => void;
}

export function GroupLensesTable({ lenses, readonly, onLensRemove, onLensChange, onLensClick }: GroupLensesTableProps) {
    const { permissions } = useSelectedOrganization();
    const onLensPriorityChange = useCallback(
        (lens: LensInGroupModel, priority: number, isValid: boolean) => {
            onLensChange({
                id: lens.id as string,
                priority,
                isValid,
                scheduleRange: lens.scheduleRange,
            });
        },
        [onLensChange],
    );
    const onLensScheduleChange = useCallback(
        (lens: LensInGroupModel, range: RangeModifier) => {
            onLensChange({
                id: lens.id as string,
                priority: lens.priority,
                isValid: lens.isValid,
                scheduleRange: range,
            });
        },
        [onLensChange],
    );
    const columns = useMemo(
        () =>
            getColumns({
                readonly,
                permissions,
                onLensRemove,
                onLensPriorityChange,
                onLensScheduleChange,
            }),
        [onLensRemove, onLensPriorityChange, onLensScheduleChange, readonly, permissions],
    );
    const onRowClick = useCallback(
        (_: unknown, rowIndex: number) => {
            onLensClick(lenses[rowIndex] as LensInGroupModel);
        },
        [onLensClick, lenses],
    );

    return (
        <AutoSizer>
            {({ width, height }: { width: number; height: number }) => (
                <VirtualTable
                    // Have to cast to non-readonly version, because SDS doesn't like that
                    data={lenses as LensInGroupModel[]}
                    columns={columns}
                    hasMore={false}
                    width={width}
                    height={height}
                    rowHeight={64}
                    rowClassName={clickableRowCss}
                    emptyMessage=""
                    loadMore={noop}
                    onRowClick={onRowClick}
                />
            )}
        </AutoSizer>
    );
}
